import styled from 'styled-components';
import { Flex, Box, Tooltip } from '@mantine/core';
import MoreIcon from '../../../../../assets/icons/more.svg?react';

import { FileCardStyle, FileContainer, MenuListStyle } from './style';
import { Popover } from 'react-tiny-popover';
import { useMemo, useRef, useState } from 'react';

import { IoIosArrowUp } from 'react-icons/io';
import { LuGripVertical, LuPlus } from 'react-icons/lu';
import { SheetSelect } from '../SheetSelect';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { SHEET_ROW_HEIGHT } from '../../utils';
import { cloneDeep } from 'lodash';
import { FiTrash2 } from 'react-icons/fi';
import ChainIcon from '../../../../../assets/icons/unmap.svg?react';
import { TbDownload, TbReplace, TbFiles, TbFile } from 'react-icons/tb';

import classNames from 'classnames';

import { newEmptySheet } from '../../hooks/useAutomationFiles';
import { MoonLoader } from 'react-spinners';
import Skeleton from 'react-loading-skeleton';
import { GoGitPullRequest } from 'react-icons/go';
import { serviceFundMapping } from '../../../../../services/funds.service';
import toast from 'react-hot-toast';
import reportStore from '../../store';
import { createStyles } from '@mantine/emotion';
import { SPREADSHEET_FILE_ACCEPT } from '../../../../../consts/fileTypes';

const useStyles = createStyles(() => ({
  uploadType: {
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4
  }
}));

export function FileCard({
  handlePreventReset,
  name,
  handleAutoMatchSheets,
  isCurrentFund = false,
  showDrag = false,
  isOpenSheet,
  onOpenSheet,
  mappedSheets = [],
  notMappedSheets = [],
  onChooseSheet,
  fileMappingId,
  previousSheets,
  isMultiUpload,
  sources,
  dragProps,
  extraRows,
  handleAddMappedSheet,
  handleSelectSource,
  onDeleteFile,
  currentFileId,
  previousFileId,
  onDeleteSheet,
  onChangeSheets,
  showPlusButton,
  handleUnmapFile,
  showAmongLine = false,
  showAmongLineReverse = false,
  isLoading,
  handleReplaceFile,
  handleDownloadFile,
  isCustomReportType,
  acceptedFileTypes = SPREADSHEET_FILE_ACCEPT,
  canManageWorkflow = true,
  previousPeriodId,
  onOpenUploadFlow,
  // Initial-setup periods (no previous_period_id) have no link to a prior
  // file, so the "Unmap" popover item and the per-file expand chevron don't
  // apply. The page-level flag is plumbed down to FileCard from FundCard.
  isInitialSetup = false
}) {
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenDeleteSheet, setIsOpenDeleteSheet] = useState(null);
  const [isAutoMatchRunning, setIsAutoMatchRunning] = useState(false);

  const { isLockedPeriod } = reportStore;

  const toggleDelete = () => {
    setIsOpenDelete(prev => !prev);
  };

  const toggleSheetDelete = value => {
    if (isOpenDeleteSheet === value) {
      setIsOpenDeleteSheet(null);
    } else {
      setIsOpenDeleteSheet(value);
    }
  };

  const onDragEndSheet = result => {
    const { source, destination } = result;

    // If there is no destination, do nothing
    if (!destination) return;

    // If the item is dropped in the same position, do nothing
    if (source.index === destination.index) return;
    // Reorder the mappedSheets array
    let updatedMappedSheets = cloneDeep(mappedSheets);

    const [removedItem] = updatedMappedSheets.splice(source.index, 1);

    updatedMappedSheets.splice(destination.index, 0, removedItem);

    updatedMappedSheets = cloneDeep(
      updatedMappedSheets.map((sheet, index) => {
        const mappedSheet = cloneDeep(previousSheets[index]);

        sheet.source = cloneDeep(mappedSheet);

        return sheet;
      })
    );

    // Call the setter or callback to update the state
    onChangeSheets(updatedMappedSheets); // Replace with your state update logic
  };

  const handleDeleteFile = () => {
    toggleDelete();
    onDeleteFile(currentFileId, name);
  };

  const onUnmapFile = () => {
    toggleDelete();
    handleUnmapFile(currentFileId, !isMultiUpload);
  };

  const onReplaceFile = e => {
    handleReplaceFile(e, currentFileId);
  };

  const onDownloadFile = () => {
    toggleDelete();
    if (isCurrentFund) {
      handleDownloadFile(currentFileId, name);
    } else {
      handleDownloadFile(previousFileId, name, previousPeriodId);
    }
  };

  const autoMatchSheets = async () => {
    setIsAutoMatchRunning(true);

    const payload = {
      previous_period_file_id: previousFileId,
      current_period_file_id: currentFileId
    };

    serviceFundMapping
      .getAutoMatchSheets(payload)
      .then(res => {
        toast.success('Auto-matching sheets have been successfully applied');
        handleAutoMatchSheets(currentFileId, previousFileId, res);
      })
      .finally(() => {
        setIsAutoMatchRunning(false);
      })
      .catch(err => {
        toast.error(
          typeof err?.data?.detail === 'string' ? err?.data?.detail : 'Failed to auto-match sheets'
        );
      });
  };

  const onAutoMatchSheets = autoMatchSheets;

  const mutatedNotMappedSheets = useMemo(() => {
    function filterUniqueArray(data) {
      const seen = new Set(); // Set to track unique entries
      const result = []; // Result array to store filtered entries

      data.forEach(item => {
        const uniqueKey = item?.id;

        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey); // Mark this combination as seen
          result.push(item); // Add to the result
        }
      });

      return result;
    }

    return filterUniqueArray([{ ...newEmptySheet }, ...(notMappedSheets || [])]);
  }, [notMappedSheets]);
  function renderExtraRows() {
    return Array(isCurrentFund ? extraRows : extraRows + 1)
      .fill()
      .map((_, index) => (
        <div key={'extra_row' + index} style={{ width: '100%', height: SHEET_ROW_HEIGHT }} />
      ));
  }

  function renderSkeleton() {
    return Array(isCurrentFund ? extraRows : extraRows + 1)
      .fill()
      .map((_, index) => (
        <StyledSkeleton key={'extra_row' + index} height={SHEET_ROW_HEIGHT} width="220px" />
      ));
  }

  const replaceInputRef = useRef();

  const onClickReplaceFile = () => {
    setIsOpenDelete(false);

    if (handlePreventReset && onOpenUploadFlow) {
      handlePreventReset(() =>
        onOpenUploadFlow({
          openLocalPicker: () => replaceInputRef.current?.click()
        })
      )();
      return;
    }

    replaceInputRef.current?.click();
  };

  const { classes } = useStyles();

  return (
    <FileContainer>
      <FileCardStyle isLoading={isLoading}>
        {!isLoading && showDrag && !isLockedPeriod && canManageWorkflow ? (
          <button {...(dragProps || {})}>
            <LuGripVertical fontSize="20px" color="#C1C2C5" />
          </button>
        ) : (
          <span style={{ width: 21.5 }} />
        )}

        {isLoading ? (
          <Flex justify="center" align="center">
            <MoonLoader size={18} color="#1971c2" />
          </Flex>
        ) : (
          // Initial-setup periods don't have a previous-file mapping to
          // expand under each row — hide the chevron entirely.
          !isInitialSetup && (
            <ToggleSheetButton onClick={onOpenSheet}>
              <IoIosArrowUp color="#C1C2C5" />
            </ToggleSheetButton>
          )
        )}

        <div
          className={classNames('file-box', {
            current: isCurrentFund
          })}
        >
          {/* {!isCurrentFund && ( */}
          {showAmongLine && (
            <div className="among-line">
              <div></div>
              <div></div>
            </div>
          )}

          {/* {isCurrentFund && ( */}
          {showAmongLineReverse && (
            <div className="among-line-reverse">
              <div></div>
              <div></div>
            </div>
          )}

          <div className="file-name">
            <p data-tooltip-content={name} data-tooltip-id="ligth-tooltip">
              {name}
            </p>

            {!isCustomReportType && (
              <Tooltip label={isMultiUpload ? 'Multi-Upload' : 'Single-Upload'}>
                <Box className={classes.uploadType} bg="#F1F3F5">
                  {isMultiUpload ? (
                    <TbFiles size={16} color="#5C5F66" />
                  ) : (
                    <TbFile size={16} color="#5C5F66" />
                  )}
                </Box>
              </Tooltip>
            )}
            {!isLockedPeriod && isCurrentFund && previousFileId && canManageWorkflow && (
              <button onClick={onAutoMatchSheets} disabled={isAutoMatchRunning}>
                {!isAutoMatchRunning ? (
                  <GoGitPullRequest />
                ) : (
                  <span className="spinner-wrapper">
                    <MoonLoader size={10} />
                  </span>
                )}
                Auto-match Sheets
              </button>
            )}
          </div>

          {/* Replace input start */}
          <input
            style={{ display: 'none' }}
            ref={replaceInputRef}
            type="file"
            multiple={false}
            accept={acceptedFileTypes}
            onChange={onReplaceFile}
          />

          {/* Replace input end */}

          {!isLoading && isCurrentFund && (
            <Popover
              isOpen={isOpenDelete}
              positions={['bottom', 'center']}
              onClickOutside={() => setIsOpenDelete(false)}
              content={
                <MenuListStyle style={{ marginRight: '60px' }}>
                  {!isLockedPeriod && canManageWorkflow && (
                    <button onClick={handlePreventReset(onClickReplaceFile)} className="menu-item">
                      <TbReplace />
                      Replace
                    </button>
                  )}
                  <button onClick={onDownloadFile} className="menu-item">
                    <TbDownload />
                    Download
                  </button>
                  {!isLockedPeriod && canManageWorkflow && !isInitialSetup && (
                    <button onClick={onUnmapFile} className="menu-item">
                      <ChainIcon />
                      Unmap
                    </button>
                  )}
                  {!isLockedPeriod && canManageWorkflow && (
                    <button onClick={handleDeleteFile} className="menu-item delete">
                      <FiTrash2 />
                      Delete
                    </button>
                  )}
                </MenuListStyle>
              }
            >
              <button onClick={toggleDelete}>
                <MoreIcon />
              </button>
            </Popover>
          )}

          {!isLoading && !isCurrentFund && previousFileId && handleDownloadFile && (
            <Popover
              isOpen={isOpenDelete}
              positions={['bottom', 'center']}
              onClickOutside={() => setIsOpenDelete(false)}
              content={
                <MenuListStyle style={{ marginRight: '60px' }}>
                  <button onClick={onDownloadFile} className="menu-item">
                    <TbDownload />
                    Download
                  </button>
                </MenuListStyle>
              }
            >
              <button onClick={toggleDelete}>
                <MoreIcon />
              </button>
            </Popover>
          )}
        </div>
      </FileCardStyle>

      {isOpenSheet && (
        <DragDropContext onDragEnd={onDragEndSheet}>
          {/* Auto-scroller disabled — matches the FundCard file-list
              Droppables. Without this, dragging a sheet close to the
              file card's edge scrolls the whole page. */}
          <Droppable
            droppableId={fileMappingId + '_sheets-list'}
            autoScrollerOptions={{ disabled: true }}
          >
            {provided => (
              <div className="sheets" {...provided.droppableProps} ref={provided.innerRef}>
                {isOpenSheet && (mappedSheets?.length > 0 || notMappedSheets?.length > 0) && (
                  <>
                    {!isLoading &&
                      mappedSheets.map((item, index) => {
                        return (
                          <Draggable
                            key={item.id || index}
                            draggableId={item.id + '_' + index}
                            index={index}
                            isDragDisabled={
                              !isCurrentFund ||
                              isLockedPeriod ||
                              !canManageWorkflow ||
                              typeof onChangeSheets !== 'function'
                            }
                          >
                            {dragProvided => (
                              <div {...dragProvided.draggableProps} ref={dragProvided.innerRef}>
                                <SheetSelect
                                  key={item.id}
                                  name={item.name}
                                  isOpenPopover={isOpenDeleteSheet === (item.id || index)}
                                  selectedSource={item.source}
                                  sheetId={item.id}
                                  options={mutatedNotMappedSheets.filter(el => !!el)}
                                  getOptionLabel={opt => opt.name}
                                  getOptionValue={opt => opt.id}
                                  isDisabled={
                                    !isCurrentFund || isLockedPeriod || !canManageWorkflow
                                  }
                                  showMore={isCurrentFund && !isLockedPeriod && canManageWorkflow}
                                  dragProps={dragProvided.dragHandleProps}
                                  sources={sources}
                                  showDrag={showDrag && mappedSheets.length > 1}
                                  hideLine={!showAmongLine}
                                  hideLineReverse={!showAmongLineReverse}
                                  onClosePopover={() => toggleSheetDelete()}
                                  showSourceSelect={isCurrentFund && !previousSheets?.[index]}
                                  handleRemove={() => onDeleteSheet(item.id, index, currentFileId)}
                                  onChangeSource={el => handleSelectSource(item.id, el)}
                                  onChange={el => onChooseSheet(index, el, previousSheets?.[index])}
                                  onClear={() => {
                                    onDeleteSheet(item.id, index, currentFileId);
                                    toggleSheetDelete();
                                  }}
                                  onClickMore={() => toggleSheetDelete(item.id || index)}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}

                    {isLoading &&
                      mappedSheets?.map((_, index) => (
                        <StyledSkeleton
                          key={'extra_row' + index}
                          height={SHEET_ROW_HEIGHT}
                          width="220px"
                        />
                      ))}

                    {provided.placeholder}

                    {!isLoading &&
                      isCurrentFund &&
                      showPlusButton &&
                      canManageWorkflow &&
                      !isLockedPeriod && (
                        // notMappedSheets.length > 0 &&
                        // !disableAdding &&
                        <div className="sheet-item">
                          <div className="sheet-wrapper">
                            <div className="control" style={{ height: 24 }}>
                              <button className="plus-btn" onClick={handleAddMappedSheet}>
                                <LuPlus />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                    {!isLoading && renderExtraRows()}

                    {isLoading && renderSkeleton()}
                  </>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </FileContainer>
  );
}

const StyledSkeleton = styled(Skeleton)`
  margin-left: 99px;
  opacity: 0.5;
`;

const ToggleSheetButton = styled.button`
  transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};
  transition: transform 0.2s ease;
`;
